import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import authService from '../../services/auth';
import apiService from '../../services/api';
import PermissionDenied from './PermissionDenied';

const ProtectedRoute = ({
  children,
  requiredPermission = null,
  adminOnly = false,
  redirectTo = '/dashboard',
}) => {
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(true);
  const [error, setError] = useState(null);
  const [alerted, setAlerted] = useState(false);

  useEffect(() => {
    const checkPermission = async () => {
      try {
        setLoading(true);
        setAlerted(false);
        setError(null);
        
        // If no specific permission is required, just check authentication
        if (!requiredPermission && !adminOnly) {
          setHasPermission(true);
          return;
        }

        const user = authService.getUser();
        if (!user) {
          setHasPermission(false);
          return;
        }

        const roleName = user.role_name?.toLowerCase();
        const isAdmin = roleName === 'admin';
        const isSuperAdmin = roleName === 'super admin';

        // Admin-only routes
        if (adminOnly) {
          if (isAdmin || isSuperAdmin) {
            setHasPermission(true);
          } else {
            setHasPermission(false);
            setError('You are not authorized to access this page');
          }
          return;
        }

        // If user is admin or super admin, they have all permissions
        if (isAdmin || isSuperAdmin) {
          setHasPermission(true);
          return;
        }

        // Check specific permission
        const response = await apiService.get(`/roles/user/${user.id}/permissions`);
        const permissions = response.data;
        
        const [module, action] = requiredPermission.split('.');
        const modulePermissions = permissions[module];
        
        if (!modulePermissions || !modulePermissions[action]) {
          setHasPermission(false);
          setError(`You don't have permission to ${action} ${module}`);
        } else {
          setHasPermission(true);
        }
      } catch (error) {
        console.error('Permission check failed:', error);
        if (error.message === 'Permission denied') {
          setHasPermission(false);
          setError('You don\'t have permission to access this page');
        } else {
          setError('Failed to check permissions');
        }
      } finally {
        setLoading(false);
      }
    };

    checkPermission();
  }, [requiredPermission, adminOnly]);

  // Check if user is authenticated
  const isAuthenticated = authService.isAuthenticated();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Checking permissions...
      </div>
    );
  }

  if (!hasPermission) {
    // For admin-only redirects (like /dashboard/users), show a popup once then redirect.
    if (adminOnly) {
      if (!alerted) {
        // eslint-disable-next-line no-alert
        window.alert(error || 'You are not authorized to access this page');
        setAlerted(true);
      }
      return <Navigate to={redirectTo} replace />;
    }

    return <PermissionDenied message={error} />;
  }

  return children;
};

export default ProtectedRoute; 